import React from 'react';
import { Gauge } from 'lucide-react';

/**
 * SEOScoreBadge — compact colour-coded pill for the SEO score, meant for
 * dense admin list rows (blog list, static pages list, categories list,
 * listings mod queue).
 *
 * The score itself is computed server-side by seo_score.py so lists remain
 * cheap. If the field is absent (public/unauthorised views), the badge
 * renders nothing rather than a misleading zero.
 *
 * Props:
 *   score  — number 0-100 (or undefined/null; renders nothing when absent)
 *   summary — optional { passed, warned, failed } counters for the tooltip
 *   size   — 'xs' (list-row default) or 'sm'
 *   className
 */

const band = (s) => {
  if (s >= 90) return { label: 'Excellent', text: 'text-emerald-800', bg: 'bg-emerald-100', border: 'border-emerald-300' };
  if (s >= 75) return { label: 'Good', text: 'text-lime-800', bg: 'bg-lime-100', border: 'border-lime-300' };
  if (s >= 50) return { label: 'Needs work', text: 'text-amber-800', bg: 'bg-amber-100', border: 'border-amber-300' };
  return { label: 'Poor', text: 'text-red-800', bg: 'bg-red-100', border: 'border-red-300' };
};

export const SEOScoreBadge = ({ score, summary, size = 'xs', className = '' }) => {
  if (score === undefined || score === null || Number.isNaN(score)) return null;
  const b = band(score);
  const tooltip = summary
    ? `SEO Score ${score}/100 (${b.label}) · ${summary.passed} passed, ${summary.warned} warnings, ${summary.failed} to fix`
    : `SEO Score ${score}/100 (${b.label})`;
  const dim = size === 'sm'
    ? 'text-[11px] px-2 py-0.5 gap-1'
    : 'text-[10px] px-1.5 py-0.5 gap-0.5';

  return (
    <span
      title={tooltip}
      aria-label={tooltip}
      data-testid='seo-score-badge'
      className={`inline-flex items-center rounded-full border font-semibold tabular-nums ${b.text} ${b.bg} ${b.border} ${dim} ${className}`}
    >
      <Gauge className={size === 'sm' ? 'h-3 w-3' : 'h-2.5 w-2.5'} />
      <span>{score}</span>
    </span>
  );
};

export default SEOScoreBadge;
